import { useState } from "react";
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  InputAdornment, MenuItem, TextField, Tooltip, Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from "recharts";
import { useTheme } from "@mui/material/styles";
import { useLanguage } from "../context/LanguageContext";
import { getRechartsTooltipProps } from "../theme";
import {
  HOURS, hourlyProfile, legacyHourlyProfile, normalizeProfile, presetProfile,
  profileTotal, profileValidation, selectedDailyPreset,
} from "./config/dailyConsumptionModel";

const LABELS = {
  daytime: ["Ημέρα", "Daytime"],
  morningEvening: ["Πρωινή και βραδινή αιχμή", "Morning and evening peaks"],
  evening: ["Βραδινή αιχμή", "Evening peak"],
  night: ["Νύχτα", "At night"],
  custom: ["Προσαρμοσμένο", "Custom"],
};

export default function DailyConsumptionModel({ demand, useCase, onChange, disabled }) {
  const { isGreek } = useLanguage();
  const theme = useTheme();
  const [editorDay, setEditorDay] = useState(null);
  const [draft, setDraft] = useState([]);
  const t = (el, en) => isGreek ? el : en;
  const shape = demand.shape;
  if (shape?.version !== 1) return null;

  const profiles = shape.dayProfiles || {};
  const customDays = shape.dayProfileCustom || {};
  const savedCustomProfiles = shape.dayCustomProfiles || {};
  const fallback = hourlyProfile(legacyHourlyProfile(demand.weights, useCase));
  const profile = (day) => hourlyProfile(profiles[day]) || fallback;
  const selectedProfile = (day) => profiles[day] && customDays[day]
    ? "custom" : selectedDailyPreset(profiles[day]);
  const convertedProfiles = () => Object.fromEntries(
    ["weekdays", "weekends"].map((day) => [day, profile(day)])
  );
  const convertedCustomDays = () => ({
    ...customDays,
    ...Object.fromEntries(["weekdays", "weekends"]
      .filter((day) => !profiles[day]).map((day) => [day, true])),
  });
  const dayLabel = (day) => day === "weekdays"
    ? t("Καθημερινή (Δευ–Παρ)", "Workday") : t("Σαββατοκύριακο", "Weekend");
  const patch = (next) => onChange({ ...shape, ...next });
  const openEditor = (day) => {
    setEditorDay(day);
    setDraft(profile(day).map(String));
  };
  const setProfile = (day, preset) => {
    const next = convertedProfiles();
    const nextCustom = convertedCustomDays();
    const nextSavedCustom = { ...savedCustomProfiles };
    if (selectedProfile(day) === "custom") nextSavedCustom[day] = profile(day);
    if (preset === "custom") {
      next[day] = hourlyProfile(nextSavedCustom[day]) || fallback;
      nextCustom[day] = true;
    } else {
      next[day] = presetProfile(preset);
      delete nextCustom[day];
    }
    patch({
      dayProfileResolution: "hourly",
      dayProfiles: next,
      dayProfileCustom: nextCustom,
      dayCustomProfiles: nextSavedCustom,
    });
  };
  const validation = editorDay ? profileValidation(draft) : null;
  const total = profileTotal(draft);
  const totalLabel = Number.isFinite(total)
    ? `${total.toLocaleString(isGreek ? "el-GR" : "en-GB", { maximumFractionDigits: 6 })}%`
    : "—";
  const saveCustom = () => {
    if (validation) return;
    const values = draft.map(Number);
    patch({
      dayProfileResolution: "hourly",
      dayProfiles: { ...convertedProfiles(), [editorDay]: values },
      dayProfileCustom: { ...convertedCustomDays(), [editorDay]: true },
      dayCustomProfiles: { ...savedCustomProfiles, [editorDay]: values },
    });
    setEditorDay(null);
  };
  const chart = (values, color, id, height) => (
    <Box sx={{ height, minWidth: 0 }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={HOURS.map((hour) => ({ time: `${String(hour).padStart(2, "0")}:00`, value: Number(values[hour]) || 0 }))}
          margin={{ top: 12, right: 10, bottom: 4, left: -16 }}>
          <defs>
            <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.18} />
              <stop offset="100%" stopColor={color} stopOpacity={0.01} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke={theme.palette.divider} vertical={false} />
          <XAxis dataKey="time" tick={{ fontSize: 11 }} interval={2} />
          <YAxis tick={{ fontSize: 11 }} width={50} unit="%" domain={[0, "auto"]} />
          <ChartTooltip {...getRechartsTooltipProps(theme.palette.mode)}
            formatter={(value) => [`${Number(value).toFixed(2)}%`, t("Μερίδιο ενέργειας", "Energy share")]} />
          <Area type="monotone" dataKey="value" stroke={color} strokeWidth={2.5}
            fill={`url(#${id})`} dot={false} isAnimationActive={false} />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );

  return (
    <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: { xs: 1.5, md: 2 }, bgcolor: "background.paper" }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "text.primary", mb: 0.5 }}>
        {t("Ημερήσιο προφίλ κατανάλωσης", "Daily energy consumption")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("Ορίστε πώς μοιράζεται η ενέργεια μέσα στο 24ωρο, ξεχωριστά για καθημερινές και Σαββατοκύριακο. Το πόση ενέργεια λαμβάνει κάθε ημέρα ρυθμίζεται στην κατανομή ημερών παραπάνω.", "Choose a separate hourly shape for workdays and weekends. The amount consumed on each day type is set by “Allocation between days” above.")}
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        {["weekdays", "weekends"].map((day) => {
          const color = day === "weekdays" ? theme.palette.primary.main : theme.palette.success.main;
          return <Box key={day} sx={{ minWidth: 0 }}>
            <Box sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
              <TextField select size="small" fullWidth label={t(`Ωριαίο σχήμα · ${dayLabel(day)}`, `${dayLabel(day)} model`)}
                value={editorDay === day ? "custom" : selectedProfile(day)} disabled={disabled}
                onChange={(event) => setProfile(day, event.target.value)}>
                {Object.entries(LABELS).map(([key, names]) =>
                  <MenuItem key={key} value={key}>{names[isGreek ? 0 : 1]}</MenuItem>)}
              </TextField>
              {selectedProfile(day) === "custom" && <Tooltip title={t(`Επεξεργασία προφίλ: ${dayLabel(day)}`, `Edit ${dayLabel(day)} profile`)} arrow>
                <span>
                  <IconButton aria-label={t(`Επεξεργασία προφίλ: ${dayLabel(day)}`, `Edit ${dayLabel(day)} profile`)}
                    disabled={disabled} onClick={() => openEditor(day)} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>}
            </Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 0.5, textAlign: "center" }}>
              {dayLabel(day)}
            </Typography>
            {chart(profile(day), color, `daily-profile-${day}`, 210)}
          </Box>;
        })}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
        {t("Οι καμπύλες δείχνουν ποσοστό της ημερήσιας ενέργειας ανά ώρα, πριν από ωράριο, αργίες, διακύμανση και φορτίο βάσης.", "Curves show each hour's share of daily energy, before schedule, holidays, variation and base load.")}
      </Typography>

      <Dialog open={Boolean(editorDay)} onClose={() => setEditorDay(null)} fullWidth maxWidth="lg"
        aria-labelledby="daily-profile-editor-title">
        <DialogTitle id="daily-profile-editor-title" sx={{ fontWeight: 700 }}>
          {t("Προσαρμοσμένο ωριαίο προφίλ", "Custom hourly profile")}{editorDay && ` · ${dayLabel(editorDay)}`}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("Συμπληρώστε το ποσοστό ενέργειας για καθεμία από τις 24 ώρες. Το 0 επιτρέπεται. Το άθροισμα πρέπει να είναι 100%.", "Enter the energy percentage for each of the 24 hours. Zero is allowed. The total must be 100%.")}
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" }, gap: 1.5 }}>
            {draft.map((value, hour) => {
              const start = `${String(hour).padStart(2, "0")}:00`;
              const end = `${String(hour + 1).padStart(2, "0")}:00`;
              return <TextField key={hour} size="small" type="number" label={`${start} – ${end}`}
                value={value} disabled={disabled}
                error={value === "" || !Number.isFinite(Number(value)) || Number(value) < 0}
                inputProps={{ min: 0, step: "any", "aria-label": `${start} ${editorDay}` }}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                onChange={(event) => setDraft((current) => current.map((entry, index) => index === hour ? event.target.value : entry))} />;
            })}
          </Box>
          <Typography role="status" variant="body2" sx={{ mt: 2, fontWeight: 700, color: validation ? "error.main" : "success.main" }}>
            {t("Άθροισμα", "Total")}: {totalLabel} / 100% · {validation
              ? t("Διορθώστε τα βάρη για να αποθηκευτεί το προφίλ.", "Correct the weights to save this profile.")
              : t("Έτοιμο για αποθήκευση.", "Ready to save.")}
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 3, mb: 0.5 }}>
            {t("Ημερήσια καμπύλη φορτίου", "Daily load curve")}
          </Typography>
          {chart(draft, editorDay === "weekdays" ? theme.palette.primary.main : theme.palette.success.main,
            "daily-profile-editor", 250)}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button disabled={disabled || !Number.isFinite(total) || total <= 0}
            onClick={() => setDraft(normalizeProfile(draft).map(String))}>
            {t("Αναγωγή σε 100%", "Normalize to 100%")}
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={() => setEditorDay(null)}>{t("Ακύρωση", "Cancel")}</Button>
          <Button variant="contained" disabled={disabled || Boolean(validation)} onClick={saveCustom}>
            {t("Αποθήκευση", "Save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
